import React, { useReducer } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";

import {
    ButtonContainer,
    SaveButton,
    SaveButtonText,
    ErrorMessage,
    SuccessMessage,
    SuccessIcon,
    Section,
    Title
} from "./styles";

import { apolloClient } from "js/apollo/apollo-client";
import { UPDATE_RECEIPT_INFO } from "./mutations";
import { VAT_PLACEHOLDER, DEFAULT_VAT_ERROR_MESSAGE } from "./data";
import { MAX_RECEIPT_CHARS } from "frontend/store/checkout";

const getInitialState = billingInfo => ({
    processing: false,
    success: false,
    error: "",
    billingInfo: String(billingInfo || "").slice(0, MAX_RECEIPT_CHARS)
});
const PROCESSING_STATE = "processing";
const SUCCESS_STATE = "success";
const ERROR_STATE = "error";
const SET_BILLING_INFO = "set-billing-info";
const formStateReducer = (state, action) => {
    if (action.type === PROCESSING_STATE) {
        return {
            ...state,
            processing: true,
            success: false,
            error: ""
        };
    }
    if (action.type === SUCCESS_STATE) {
        return {
            ...state,
            processing: false,
            success: true,
            error: ""
        };
    }
    if (action.type === ERROR_STATE) {
        return {
            ...state,
            processing: false,
            success: false,
            error: action.value || DEFAULT_VAT_ERROR_MESSAGE
        };
    }
    if (action.type === SET_BILLING_INFO) {
        return {
            ...state,
            billingInfo: String(action.value).slice(0, MAX_RECEIPT_CHARS)
        };
    }
};

const InvoiceInformation = ({
    canSaveVAT = false,
    billingInfo: savedBillingInfo = "",
    organizationId = null
}) => {
    const initialState = getInitialState(savedBillingInfo);
    const [state, dispatch] = useReducer(formStateReducer, initialState);

    const [saveBillingInfo] = useMutation(UPDATE_RECEIPT_INFO, {
        client: apolloClient,
        variables: {
            receiptInfo: state.billingInfo,
            organizationId
        }
    });

    if (!canSaveVAT) {
        return null;
    }

    const buttonOnClick = async () => {
        if (state.processing) {
            return;
        }

        try {
            dispatch({ type: PROCESSING_STATE });
            const res = await saveBillingInfo();
            if (
                res.data &&
                res.data.updateReceiptInfo &&
                res.data.updateReceiptInfo.ok
            ) {
                dispatch({ type: SUCCESS_STATE });
            } else {
                dispatch({ type: ERROR_STATE });
            }
        } catch (e) {
            dispatch({ type: ERROR_STATE });
        }
    };

    return (
        <Section>
            <Title>Optional Invoice Information</Title>
            <ol>
                <li>
                    <textarea
                        id="receipt-info"
                        className="optional-vat"
                        placeholder={VAT_PLACEHOLDER}
                        value={state.billingInfo}
                        disabled={state.processing}
                        onChange={e =>
                            dispatch({
                                type: SET_BILLING_INFO,
                                value: e.target.value
                            })
                        }
                    />
                    <label
                        htmlFor="receipt-info"
                        data-label-text="Optional Invoice Information"
                    >
                        Optional Invoice Information
                    </label>
                </li>
            </ol>
            <ButtonContainer>
                <SaveButton onClick={buttonOnClick}>
                    <SaveButtonText>Save</SaveButtonText>
                </SaveButton>
                {!!state.error && <ErrorMessage>{state.error}</ErrorMessage>}
                {state.success && (
                    <SuccessMessage>
                        <SuccessIcon />
                        Saved successfully
                    </SuccessMessage>
                )}
            </ButtonContainer>
        </Section>
    );
};

InvoiceInformation.propTypes = {
    canSaveVAT: PropTypes.bool.isRequired,
    billingInfo: PropTypes.string,
    organizationId: PropTypes.number
};

export default InvoiceInformation;
